import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  Grid,
  Paper,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error,
  Refresh,
  TrendingUp,
} from '@mui/icons-material';
import { eligibilityMigrationService, MigrationStatistics } from '../../services/eligibilityMigrationService';

const BulkMigrationManager: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<MigrationStatistics | null>(null);
  const [migrationResult, setMigrationResult] = useState<any>(null);

  /**
   * Load migration statistics
   */
  const loadStatistics = async () => {
    try {
      // Check if user is authenticated
      const { auth } = await import('../../firebase');
      const user = auth.currentUser;
      console.log('🔐 Current user:', user ? user.uid : 'Not authenticated');
      
      if (!user) {
        setError('User not authenticated. Please log in.');
        return;
      }
      
      const stats = await eligibilityMigrationService.getMigrationStatistics();
      setStatistics(stats);
    } catch (err: any) {
      console.error('Error loading statistics:', err);
      setError(`Failed to load migration statistics: ${err.message}`);
    }
  };

  /**
   * Trigger bulk migration
   */
  const triggerBulkMigration = async () => {
    try {
      setIsLoading(true);
      setIsRunning(true);
      setError(null);
      setSuccess(null);
      setMigrationResult(null);

      console.log('🚀 Starting bulk migration...');
      
      const result = await eligibilityMigrationService.triggerBulkMigration();
      
      setMigrationResult(result);
      setSuccess('Bulk migration completed successfully!');
      
      // Refresh statistics
      await loadStatistics();
      
      console.log('✅ Bulk migration completed:', result);
    } catch (err: any) {
      console.error('❌ Bulk migration failed:', err);
      setError(err.message || 'Bulk migration failed');
    } finally {
      setIsLoading(false);
      setIsRunning(false);
    }
  };

  // Load statistics on mount
  useEffect(() => {
    loadStatistics();
  }, []);

  const migrationProgress = statistics ? 
    Math.round((statistics.migratedUsers / statistics.totalUsers) * 100) : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Eligibility Data Migration
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Migrate all existing user eligibility data to the new Firebase cache system.
        This enables the gradual rollout with fallback approach.
      </Typography>

      {/* Statistics Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Migration Statistics
          </Typography>
          
          {statistics ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    {statistics.totalUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {statistics.migratedUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Migrated Users
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">
                    {statistics.skippedUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Skipped Users
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main">
                    {statistics.totalPrograms}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Programs
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <CircularProgress size={24} />
          )}

          {/* Progress Bar */}
          {statistics && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Migration Progress
                </Typography>
                <Chip 
                  label={`${migrationProgress}%`} 
                  size="small" 
                  color={migrationProgress === 100 ? 'success' : 'primary'}
                  sx={{ ml: 1 }}
                />
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={migrationProgress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Migration Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Migration Actions
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={triggerBulkMigration}
              disabled={isLoading || isRunning}
              color="primary"
            >
              {isRunning ? 'Running Migration...' : 'Start Bulk Migration'}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadStatistics}
              disabled={isLoading}
            >
              Refresh Statistics
            </Button>
          </Box>

          {isRunning && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Migration is running in the background. This may take several minutes 
                depending on the number of users. You can refresh the statistics to 
                check progress.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Error:</strong> {error}
          </Typography>
        </Alert>
      )}

      {/* Success Display */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Success:</strong> {success}
          </Typography>
        </Alert>
      )}

      {/* Migration Result */}
      {migrationResult && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Migration Results
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Migrated Users:</strong> {migrationResult.migratedUsers}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUp color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    <strong>Total Programs:</strong> {migrationResult.totalPrograms}
                  </Typography>
                </Box>
              </Grid>
              
              {migrationResult.skippedUsers > 0 && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Error color="warning" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="warning.main">
                      <strong>Skipped Users:</strong> {migrationResult.skippedUsers}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {migrationResult.failedUsers > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Error color="error" sx={{ mr: 1 }} />
                    <Typography variant="body2" color="error">
                      <strong>Failed Users:</strong> {migrationResult.failedUsers}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>

            {migrationResult.errors && migrationResult.errors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Errors:
                </Typography>
                {migrationResult.errors.slice(0, 5).map((error: any, index: number) => (
                  <Typography key={index} variant="caption" display="block" color="error">
                    User {error.userId}: {error.error}
                  </Typography>
                ))}
                {migrationResult.errors.length > 5 && (
                  <Typography variant="caption" color="error">
                    ... and {migrationResult.errors.length - 5} more errors
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BulkMigrationManager;
